<template>
  <div class="view-header">
    <div class="header-left">
      <button
        class="back-arrow"
        :aria-label="$t('common.back')"
        @click="$emit('go-back')"
      >
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          stroke="currentColor"
          stroke-width="3"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>
      <div class="lottery-info">
        <div class="flag-container">
          <CountryAvatar
            v-if="isLite"
            :country="lottery?.country"
            :fallback-label="lottery?.game_name"
            size="sm"
          />
          <img 
            v-else-if="lottery?.game_logo" 
            :src="getGameLogoUrl(lottery.game_logo, lottery.game_slug)" 
            :alt="lottery.country + ' flag'" 
            class="flag" 
          >
        </div>
        <div class="lottery-title-row">
          <h1 class="lottery-name">
            {{ lottery?.game_name || $t('common.loading') }}
          </h1>
          <button
            v-if="showWatchLive"
            type="button"
            class="watch-live-btn"
            @click="$emit('watch-live')"
          >
            <span class="watch-live-icon" aria-hidden="true">▶</span>
            {{ $t('detail.watchLive') }}
          </button>
        </div>
      </div>
    </div>

    <div class="countdown-section">
      <div class="countdown">
        <div class="time-block">
          <div class="time">
            {{ String(countdown.hours).padStart(2, '0') }}
          </div>
          <div class="label">
            {{ $t('time.hours') }}
          </div>
        </div>
        <div class="time-block">
          <div class="time">
            {{ String(countdown.minutes).padStart(2, '0') }}
          </div>
          <div class="label">
            {{ $t('time.mins') }}
          </div>
        </div>
        <div class="time-block">
          <div class="time">
            {{ String(countdown.seconds).padStart(2, '0') }}
          </div>
          <div class="label">
            {{ $t('time.secs') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getGameLogoUrl } from '../../utils/resultParser';
import { useIntegrationMode } from '@/composables/useIntegrationMode';
import CountryAvatar from '@/components/base/CountryAvatar.vue';

const { isLite } = useIntegrationMode();

interface Lottery {
  game_name?: string;
  game_slug?: string;
  country?: string;
  game_logo?: string;
  draw_date?: string;
  cutoff?: string;
}

interface Props {
  lottery: Lottery | null;
  countdown: {
    hours: number;
    minutes: number;
    seconds: number;
  };
  showWatchLive?: boolean;
}

defineProps<Props>();

defineEmits<{
  'go-back': [];
  'watch-live': [];
}>();

</script>

<script lang="ts">
export default {
  name: "DesktopHeader",
};
</script>

<style scoped>
.view-header {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  background: var(--surface-detail-game-header);
  padding: 0;
  margin-bottom: 8px;
  min-height: 60px;
  width: 100%;
}

.header-left {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  padding-right: 16px;
}

.back-arrow {
  background: transparent;
  border: none;
  color: var(--icon-detail-game-header);
  padding: 0 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  flex-shrink: 0;
  transition: background 0.2s;
}

.back-arrow:hover {
  background: var(--colour-surface-cont);
}

.lottery-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.lottery-title-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.flag-container {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--detail-header-flag-bg, var(--colour-surface-cont-lowest));
  border: 1px solid var(--detail-header-flag-border, var(--dropdown-border));
}

.flag {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lottery-name {
  color: var(--text-detail-game-header);
  font-size: 18px;
  font-weight: 400;
  font-style: normal;
  margin: 0;
  font-family: 'Ubuntu', sans-serif;
}

.watch-live-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: transparent;
  color: var(--accent-color);
  border: 1px solid var(--accent-color);
  border-radius: 4px;
  padding: 0.25rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
}

.watch-live-btn:hover {
  background: rgba(255, 255, 255, 0.06);
}

.watch-live-icon {
  font-size: 0.7rem;
  color: #22c55e;
  line-height: 1;
}

.countdown-section {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-detail-countdown);
  padding: 0 24px;
  min-height: 60px;
  flex-shrink: 0;
}

.countdown {
  display: flex;
  gap: 16px;
  align-items: center;
}

.time-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 45px;
}

.time {
  color: var(--text-detail-countdown, var(--text-detail-game-header, var(--colour-text-inverse)));
  text-align: center;
  font-family: var(--typography-font-family, 'Ubuntu', sans-serif);
  font-size: var(--detail-countdown-time-size, 21.961px);
  font-style: normal;
  font-weight: 700;
  line-height: normal;
}

.label {
  color: var(--text-detail-countdown-muted);
  font-size: 10px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0.5px;
  margin-top: 4px;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .view-header {
    min-height: 50px;
  }
  .lottery-name {
    font-size: 16px;
  }
  .back-arrow {
    height: 50px;
    padding: 0 12px;
  }
  .countdown-section {
    height: 50px;
    padding: 0 12px;
  }
  .label {
    font-size: 8px;
  }
}
</style>
